import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, Check } from "lucide-react";
import { relativeMinutes } from "@/lib/format";
import { toast } from "sonner";

export default function NotificationsBell() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);

    const fetchItems = useCallback(async () => {
        try {
            const { data } = await api.get("/notifications");
            setItems(data || []);
        } catch (error) {
            // Notifications are non-critical; degrade silently in UI but log for ops.
            console.warn("[notifications] fetch failed", error?.message);
        }
    }, []);

    useEffect(() => {
        fetchItems();
        const id = setInterval(fetchItems, 8000);
        return () => clearInterval(id);
    }, [fetchItems]);

    const unread = items.filter(i => !i.read).length;

    const markAll = async () => {
        try {
            await api.post("/notifications/read-all");
            fetchItems();
        } catch (error) {
            console.warn("[notifications] mark-all failed", error?.message);
            toast.error("Could not update notifications");
        }
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="notifications-bell" className="relative">
                    <Bell className="h-5 w-5" />
                    {unread > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-[hsl(var(--brand-terracotta))] text-[10px] text-white grid place-items-center px-1" data-testid="notifications-unread-count">
                            {unread}
                        </span>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0">
                <div className="px-3 py-2 border-b border-border flex items-center justify-between">
                    <div className="font-medium">Notifications</div>
                    {unread > 0 && (
                        <Button variant="ghost" size="sm" onClick={markAll} data-testid="notifications-mark-all">
                            <Check className="h-4 w-4 mr-1" /> Mark all read
                        </Button>
                    )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                    {items.length === 0 ? (
                        <div className="p-6 text-center text-sm text-muted-foreground">You're all caught up.</div>
                    ) : items.map(n => (
                        <div key={n.id} className={`px-3 py-2 border-b border-border/60 last:border-0 ${!n.read ? "bg-accent/30" : ""}`} data-testid="notification-item">
                            <div className="text-sm font-medium">{n.title}</div>
                            <div className="text-xs text-muted-foreground">{n.body}</div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground/80 mt-1">{relativeMinutes(n.created_at)}</div>
                        </div>
                    ))}
                </div>
                <div className="px-3 py-2 border-t border-border">
                    <Link to="/notifications" className="text-xs text-primary hover:underline" onClick={() => setOpen(false)}>View all</Link>
                </div>
            </PopoverContent>
        </Popover>
    );
}
